import { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { activateBody, startPlateBody } from '../quickActions';
import { FormButtons, FormError, grid, stack, useQuickSubmit } from './shared';
import * as S from '../../specimens/styles';
import MediumOptions from '../../culture/MediumOptions';
import PlateStartFields, { EMPTY_START } from '../../culture/PlateStartFields';
import { isPreset, mediumName } from '../../../utils/cultureMedia';

/*
 * Start plates for an isolate that has none (POST /plates), or -- with
 * `activate` -- make the two plates a submitted collection is waiting for
 * (PUT /plates/sample/{id}/activate).
 *
 * A collection's second plate takes the species' known medium only when
 * the person accepts it (GET /taxonomy/protocol-lookup), else a medium they
 * choose; the first is always the generic control (plates.py:308-338). An
 * intake's plate takes the donor's medium, and may add a control plate.
 *
 * The suggestion is looked up for the name the server will look up: the
 * collection's current name, and none when its rank is 'unknown'
 * (plates.py:265-273, 678-683) -- not the isolate's own name, which can be
 * older. A suggestion offered here is the medium the plates get.
 *
 * A find isolated in the field during a live event (routes/field_isolation.py,
 * PR11) says so here: its dish (PDA or MYA, the antibiotics, the label) fills
 * the notes, and its medium the medium when no suggestion was found -- the
 * isolation joins the plates through the same sample_id, no column of its own.
 */
export default function PlateStartForm({ item, protocols, t, activate = false, onDone, onCancel }) {
  const field = !!item.sample_id;
  const [lookup, setLookup] = useState(null);
  const [isolation, setIsolation] = useState(null);
  const [form, setForm] = useState({ accept_suggested: false, protocol: '', temperature_c: 25, control_pair: false, notes: '', start: EMPTY_START });
  const { busy, error, setError, send } = useQuickSubmit(activate ? 'hub-activate' : 'hub-plates', {
    // The server found no suggestion after all: the medium select comes back,
    // with the reason in the reader's words.
    onError: (e) => {
      if (e?.response?.status === 400 && e.response.data?.protocol_lookup?.found === false) {
        setLookup(null);
        setForm((f) => ({ ...f, accept_suggested: false }));
        setError(t('quick.problems.protocolRequired'));
      }
    },
  });
  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  useEffect(() => {
    if (!field) return undefined;
    let live = true;
    api.get(`/collections/${item.sample_id}`)
      .then((res) => {
        const c = res?.data || {};
        const name = c.current_rank === 'unknown' ? null : c.current_taxon_name;
        if (!live || !name) return null;
        return api.get('/taxonomy/protocol-lookup', { params: { taxon_name: name } });
      })
      .then((res) => {
        if (!live || !res?.data?.found) return;
        setLookup(res.data);
        setForm((f) => ({ ...f, accept_suggested: true,
          temperature_c: res.data.temperature_c != null ? res.data.temperature_c : f.temperature_c }));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [field, item.sample_id]);

  // A field isolation of this find, best effort (lab:read; nothing for anyone else).
  useEffect(() => {
    if (!field) return undefined;
    let live = true;
    api.get(`/collections/${item.sample_id}/isolations`)
      .then((res) => {
        const last = (res?.data?.items || []).slice(-1)[0];
        if (!live || !last) return;
        setIsolation(last);
        const words = [`${last.medium}${last.antibiotics?.length ? ` + ${last.antibiotics.join(', ')}` : ''}`,
          last.dish_label].filter(Boolean).join(' · ');
        setForm((f) => ({
          ...f,
          notes: f.notes || t('quick.fieldIsolationNote', { dish: words }),
          // The dish's medium and antibiotics are one of the four standard media
          // (utils/cultureMedia.js), or a medium the lab already lists.
          protocol: f.protocol || (() => {
            const name = mediumName(last.medium, last.antibiotics);
            return isPreset(name) || (protocols || []).some((p) => (p.name || p) === name) ? name : '';
          })(),
        }));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [field, item.sample_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_suggested && !form.protocol) {
      setError(t('quick.problems.protocolRequired'));
      return;
    }
    const res = activate
      ? await send('put', `/plates/sample/${item.sample_id}/activate`, activateBody(form))
      : await send('post', '/plates', startPlateBody(form, item));
    if (!res) return;
    const codes = (res.data?.plates || []).map((p) => p.plate_code).filter(Boolean).join(', ');
    const said = activate ? t('toast.platesActivated') : t('toast.platesStarted');
    onDone(codes ? `${said} (${codes})` : said);
  };

  return (
    <form onSubmit={submit} style={stack} data-testid={activate ? 'quick-activate' : 'quick-start-plate'}>
      {field && <p style={S.hint}>{t('quick.plates.controlNote')}</p>}
      {isolation && (
        <p style={S.hint} data-testid="quick-field-isolation">
          {t('quick.fieldIsolation', { medium: isolation.medium, label: isolation.dish_label || '' })}
        </p>
      )}
      {lookup && (
        <label style={{ ...S.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={form.accept_suggested} onChange={set('accept_suggested')}
            title={t('tip.quick.acceptSuggested')} data-testid="quick-accept-suggested" />
          {t('quick.plates.useSuggested', { protocol: lookup.protocol })}
        </label>
      )}
      <div style={grid}>
        {!form.accept_suggested && (
          <label style={S.label}>
            {t('quick.plates.medium')} *
            <select value={form.protocol} onChange={set('protocol')} style={S.input} aria-required="true"
              title={t('tip.quick.medium')} data-testid="quick-medium">
              <option value="">{t('common:selectPlaceholder')}</option>
              <MediumOptions protocols={protocols} />
            </select>
          </label>
        )}
        <label style={S.label}>
          {t('quick.plates.temperature')}
          <input type="number" step="0.5" value={form.temperature_c} onChange={set('temperature_c')} style={S.input}
            title={t('tip.quick.temperature')} />
        </label>
      </div>
      <div style={grid}>
        <PlateStartFields value={form.start} labelStyle={S.label} inputStyle={S.input} testIdPrefix="quick-plate-start"
                          onChange={patch => setForm(f => ({ ...f, start: { ...f.start, ...patch } }))} />
      </div>
      {!field && !activate && (
        <label style={{ ...S.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={form.control_pair} onChange={set('control_pair')} title={t('tip.quick.controlPair')} />
          {t('quick.plates.controlPair')}
        </label>
      )}
      {activate && (
        <label style={S.label}>
          {t('quick.notes')}
          <textarea rows={2} value={form.notes} onChange={set('notes')} style={S.input} title={t('tip.quick.notes')} />
        </label>
      )}
      <FormError>{error}</FormError>
      <FormButtons busy={busy} onCancel={onCancel} t={t}
        saveLabel={activate ? t('quick.plates.activate') : t('quick.plates.start')} />
    </form>
  );
}
